import {
  Backdrop,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStores } from "../../../../use-stores";
import CustomerHeading from "../../../components/projects/projectDetails/CustomerHeading";
import CustomTypography from "../../../components/common/form/CustomTypography";
import CustomTextField from "../../../components/common/textFields/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import AppButton from "../../../components/common/buttons/AppButton";
import SelectProductPopup from "./SelectProductPopup";
import CheckCircleIcon from "../../../../assets/sucessicon.png";
import CustomSucessPopup from "../../../components/common/pop-up/CustomSucessPopup";
import CustomToggleSwitch from "../../../components/common/switch/Switch";
import {
  PackCreationProduct,
  PackCreationData,
  ProductData,
} from "../../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import { useNotification } from "../../../../utils/NotificationsContext";
import { PartnerCustomerInventoryTagsStore } from "../../../../stores/PartnerCustomerInventoryTagsStore";
import AppSearchDropdownMulti from "../../../components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../models/Filters/FilterValue";

const partnerCustomerInventoryTagsStore =
  new PartnerCustomerInventoryTagsStore();

const PACK_CLASSIFICATION_OPTIONS = [
  { id: "S", value: "Small" },
  { id: "M", value: "Medium" },
  { id: "L", value: "Large" },
  { id: "XL", value: "Xtra Large" },
  { id: "SP", value: "Swagpack" },
];

const resolveClassificationOption = (
  rawClassification?: string,
): FilterValue | undefined => {
  if (!rawClassification) return undefined;
  const normalized = String(rawClassification).trim().toUpperCase();
  const aliases: Record<string, string> = {
    SMALL: "S",
    MEDIUM: "M",
    LARGE: "L",
    "XTRA LARGE": "XL",
    "EXTRA LARGE": "XL",
    SWAGPACK: "SP",
  };
  const resolvedId = aliases[normalized] || normalized;
  return (
    PACK_CLASSIFICATION_OPTIONS.find((option) => option.id === resolvedId) ||
    PACK_CLASSIFICATION_OPTIONS.find(
      (option) => option.value.toUpperCase() === normalized,
    )
  );
};

const getClassificationSelection = (
  value: FilterValue | string | undefined,
): FilterValue[] | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") {
    const option = resolveClassificationOption(value);
    return option ? [option] : undefined;
  }
  const byId = resolveClassificationOption(String(value.id));
  if (byId) return [byId];
  if (value.value) {
    const byLabel = resolveClassificationOption(String(value.value));
    if (byLabel) return [byLabel];
  }
  return value.id && value.value ? [value] : undefined;
};

const CreatePack = () => {
  const theme = useTheme();
  const { projectPackagingOpsStore } = useStores();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [searchParams] = useSearchParams();
  const projectId = Number(searchParams.get("project-id"));
  const [productPopupOpen, setProductPopupOpen] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState<{
    [key: string]: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [open, setOpen] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState<ProductData[]>([]);
  const [filteredProductsForPopup, setFilteredProductsForPopup] = useState<
    ProductData[]
  >([]);
  const [variantSections, setVariantSections] = useState<{
    [key: number]: number[];
  }>({});
  const [availableQuantities, setAvailableQuantities] = useState<
    Record<number, number>
  >({});
  const [packName, setPackName] = useState("");
  const [totalPacksQuantity, setTotalPacksQuantity] = useState<number | "">(1);
  const [selectedInventoryTag, setSelectedInventoryTag] = useState<
    FilterValue | undefined
  >(undefined);
  const [variantPackSizes, setVariantPackSizes] = useState<{
    [key: string]: number;
  }>({});
  const [variantClassifications, setVariantClassifications] = useState<{
    [key: string]: FilterValue | undefined;
  }>({});
  const [createPackWithExistingId, setCreatePackWithExistingId] = useState(false);

  const productIdsFromUrl =
    (searchParams
      .get("productId")
      ?.split(",")
      .map((id) => {
        const num = Number(id);
        return isNaN(num) ? null : num;
      })
      .filter((id) => id !== null) as number[]) || [];
  const popupMode = productIdsFromUrl?.length === 1 ? "bulk" : "pack";
  const existingSwagpackId = searchParams.get("swagpack_id")
    ? Number(searchParams.get("swagpack_id"))
    : null;
  const fromExisting = !!existingSwagpackId;

  // Helper functions to reduce duplication
  const calculateAvailableQuantities = async () => {
    const result = projectPackagingOpsStore.filterProductsForPopup();
    return result.reduce(
      (acc, product) => {
        acc[product.productId] = product.availableQuantity || 0;
        return acc;
      },
      {} as Record<number, number>,
    );
  };

  const handleError = (error: any, defaultMessage: string) => {
    const errorMessage =
      error.response?.data?.message || error.message || defaultMessage;
    showError("Error", errorMessage);
  };

  const getExistingSwagpack = () => {
    return projectPackagingOpsStore.packagingRequestBody.swagpacks?.find(
      (swagpack) => swagpack.swagpack_id === existingSwagpackId,
    );
  };

  const getExistingProduct = (productId: number) => {
    const existingSwagpack = getExistingSwagpack();
    return existingSwagpack?.products.find((sp) => sp.product_id === productId);
  };

  const getVariantStateKey = (productId: number, variantKey?: number) =>
    variantKey ? `${productId}_${variantKey}` : `${productId}_main`;

  const initializeProductData = (product: ProductData) => {
    const initialQuantity = fromExisting ? 0 : 1;
    let initialVariant = "";

    if (product.options?.Size?.length === 1) {
      const sizeId = product.options.Size[0].id.toString();
      initialVariant = sizeId;
      if (!fromExisting) {
        projectPackagingOpsStore.generateSku(product.productId, sizeId);
      }
    } else if (product.options?.Size?.length > 1) {
      projectPackagingOpsStore.clearSkuForProduct(product.productId);
    }

    return { initialQuantity, initialVariant };
  };

  const validateProductVariant = (product: ProductData, prId: number) => {
    if (fromExisting && popupMode === "bulk") {
      return true;
    }

    if (product.options?.Size && product.options.Size?.length > 1) {
      if (!selectedVariants[prId]) {
        showError(
          "Validation Error",
          `Please select a variant for ${product.name}`,
        );
        return false;
      }
    }
    return true;
  };

  const validateProductQuantity = (product: ProductData, prId: number) => {
    if (!quantities[prId] || quantities[prId] < 1) {
      showError(
        "Validation Error",
        `Quantity for ${product.name} must be at least 1`,
      );
      return false;
    }
    return true;
  };

  const calculateRequiredQuantityPerPack = (
    product: ProductData,
    prId: number,
  ) => {
    let requiredQtyPerPack = 0;

    if (fromExisting && popupMode === "pack") {
      const existingProduct = getExistingProduct(prId);
      if (existingProduct && existingProduct.variants?.length > 0) {
        requiredQtyPerPack = existingProduct.variants.reduce(
          (total, variant) => total + (variant.quantity || 0),
          0,
        );
      }
    } else {
      if (
        product.options?.Size &&
        product.options.Size?.length > 1 &&
        variantSections[prId]
      ) {
        requiredQtyPerPack = quantities[prId] || 0;
        for (const variantKey of variantSections[prId]) {
          requiredQtyPerPack += quantities[`${prId}_${variantKey}`] || 0;
        }
      } else {
        requiredQtyPerPack = quantities[prId] || 0;
      }
    }

    return requiredQtyPerPack;
  };

  const validateStockAvailability = (
    product: ProductData,
    prId: number,
    effectiveTotalPacksQuantity: number,
  ) => {
    const requiredQtyPerPack = calculateRequiredQuantityPerPack(product, prId);
    const packsToAdd = popupMode === "bulk" ? 1 : effectiveTotalPacksQuantity;
    const totalRequiredQty = requiredQtyPerPack * packsToAdd;

    if (totalRequiredQty > availableQuantities[prId]) {
      showError(
        "Validation Error",
        `Insufficient stock for "${product.name}". Required: ${totalRequiredQty}, Available: ${availableQuantities[prId]}`,
      );
      return false;
    }
    return true;
  };

  const validateVariantSections = (product: ProductData, prId: number) => {
    // Skip variant sections validation for existing bulk mode
    if (fromExisting && popupMode === "bulk") {
      return true;
    }

    if (variantSections[prId]) {
      for (const variantKey of variantSections[prId]) {
        if (
          !selectedVariants[`${prId}_${variantKey}`] &&
          product.options?.Size?.length > 1
        ) {
          showError(
            "Validation Error",
            `Please select a variant for all sizes of ${product.name}`,
          );
          return false;
        }

        const variantQuantity = quantities[`${prId}_${variantKey}`];
        if (typeof variantQuantity !== "number" || variantQuantity < 1) {
          showError(
            "Validation Error",
            `Quantity for each variant of ${product.name} must be at least 1`,
          );
          return false;
        }
      }
    }
    return true;
  };

  const createPackData = (
    products: PackCreationProduct[],
    effectiveTotalPacksQuantity: number,
  ): PackCreationData => {
    const selectedTag =
      partnerCustomerInventoryTagsStore.partnerCustomerInventoryTags.find(
        (tag) => String(tag.id) === selectedInventoryTag?.id,
      );

    return {
      inventory_id:
        projectPackagingOpsStore.packagingRequestBody.inventory_id || 0,
      customerId: projectPackagingOpsStore.packagingRequestBody.customerId || 0,
      order_id: String(
        projectPackagingOpsStore.packagingRequestBody.project_id || "",
      ),
      pack_id: fromExisting ? existingSwagpackId : existingSwagpackId || 0,
      create_new_pack: createPackWithExistingId ? 1 : 0,
      packName: packName.trim(),
      partner_customer_inventory_tag: selectedTag?.tag_id,
      pack_image_url: null,
      packs_count: effectiveTotalPacksQuantity,
      products: products,
    };
  };

  const handleSwagPackCreation = (
    packData: PackCreationData,
    isExisting: boolean,
  ) => {
    projectPackagingOpsStore
      .createSwagPack(packData, projectId)
      .then(() => {
        setOpen(true);
      })
      .catch((error: any) => {
        const defaultMessage = isExisting
          ? "Failed to create/copy swagpack. Please try again."
          : "Failed to create pack. Please try again.";
        handleError(error, defaultMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getVariantQuantity = (
    product: ProductData,
    prId: number,
    variantKey?: number,
    effectiveTotalPacksQuantity?: number,
  ) => {
    // In existing bulk mode, zero means "no quantity add", so keep qty at 0.
    // In existing pack mode, we still need variants for config updates.
    if (
      fromExisting &&
      popupMode === "bulk" &&
      effectiveTotalPacksQuantity === 0
    ) {
      return 0;
    } else if (fromExisting && popupMode === "pack") {
      const existingProduct = getExistingProduct(prId);
      if (
        existingProduct &&
        existingProduct.variants?.length > 1 &&
        variantKey
      ) {
        const variantSizeId =
          selectedVariants[`${prId}_${variantKey}`] ||
          product.options?.Size?.[0]?.id.toString();
        const variantSizeName =
          product.options?.Size?.find(
            (size) => size.id.toString() === variantSizeId,
          )?.name || "";
        const matchingVariant = existingProduct.variants.find(
          (v) => v.size.toLowerCase() === variantSizeName.toLowerCase(),
        );
        return matchingVariant
          ? matchingVariant.quantity || 1
          : quantities[`${prId}_${variantKey}`] || 1;
      } else if (existingProduct && existingProduct.variants?.length > 0) {
        return existingProduct.variants[0].quantity || 1;
      }
    }
    return variantKey
      ? quantities[`${prId}_${variantKey}`] || 1
      : quantities[prId] || 1;
  };

  const getVariantSizeInfo = (
    product: ProductData,
    prId: number,
    variantKey?: number,
    effectiveTotalPacksQuantity?: number,
  ) => {
    const sizes = product.options?.Size || [];
    let sizeId = variantKey
      ? selectedVariants[`${prId}_${variantKey}`] || sizes[0]?.id.toString()
      : selectedVariants[prId] || sizes[0]?.id.toString();
    let sizeName =
      sizes.find((size) => size.id.toString() === sizeId)?.name || "";

    if (
      fromExisting &&
      popupMode === "pack" &&
      effectiveTotalPacksQuantity &&
      effectiveTotalPacksQuantity > 0
    ) {
      const existingProduct = getExistingProduct(prId);
      if (existingProduct && existingProduct.variants?.length > 0) {
        const variantIndex = variantKey
          ? variantSections[prId]?.indexOf(variantKey) + 1
          : 0;
        const existingVariant =
          variantIndex > 0
            ? existingProduct.variants[variantIndex]
            : existingProduct.variants[0];
        if (existingVariant) {
          sizeName = existingVariant.size || "";
          const matchingSize = sizes.find(
            (size) =>
              size.name.toLowerCase() === existingVariant.size.toLowerCase(),
          );
          if (matchingSize) {
            sizeId = matchingSize.id.toString();
          }
        }
      }
    }

    return { sizeId, sizeName };
  };

  const createVariantObject = (
    quantity: number,
    sizeName: string,
    packSize: number,
    classification: string | undefined,
    colors: any[],
    sku: string,
  ) => {
    const variant: {
      variant_quantity: number;
      size: string;
      pack_size: number;
      box_size: "S" | "M" | "L" | "XL" | "SP" | undefined;
      color?: string;
      client_sku?: string;
    } = {
      variant_quantity: quantity,
      size: sizeName,
      pack_size: packSize,
      box_size: classification as "S" | "M" | "L" | "XL" | "SP" | undefined,
    };

    if (colors?.length > 0 && colors[0]?.id) {
      variant.color = colors[0].id.toString();
    }
    if (sku) {
      variant.client_sku = sku;
    }

    return variant;
  };

  useEffect(() => {
    setIsLoading(true);

    // Check if we have the required data, if not fetch it

    if (
      !projectPackagingOpsStore.packagingRequestBody.products ||
      projectPackagingOpsStore.packagingRequestBody.products?.length === 0 ||
      !projectPackagingOpsStore.packagingSourcingSwagpacks ||
      projectPackagingOpsStore.packagingSourcingSwagpacks?.length === 0
    ) {
      Promise.all([
        projectPackagingOpsStore.getProjectPackagingOpsDetails(projectId),
        projectPackagingOpsStore.getSourcingSwagpacks(projectId),
      ])
        .then(() => {
          // Now calculate available quantities after data is loaded
          return calculateAvailableQuantities();
        })
        .then((quantities) => {
          setAvailableQuantities(quantities);
        })
        .catch((error) =>
          handleError(
            error,
            "Failed to fetch available quantities. Please try again.",
          ),
        )
        .finally(() => setIsLoading(false));
    } else {
      // Data is already available, calculate available quantities
      calculateAvailableQuantities()
        .then((quantities) => {
          setAvailableQuantities(quantities);
        })
        .catch((error) =>
          handleError(
            error,
            "Failed to fetch available quantities. Please try again.",
          ),
        )
        .finally(() => setIsLoading(false));
    }

    if (!fromExisting) {
      projectPackagingOpsStore.clearAllSkus();
    }
    projectPackagingOpsStore.setPackNameManuallyEdited(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const customerId = projectPackagingOpsStore.packagingRequestBody.customerId;
    if (!customerId) return;

    partnerCustomerInventoryTagsStore
      .getPartnerCustomerInventoryTagsByCustomerId(customerId)
      .then(() => {
        const tags =
          partnerCustomerInventoryTagsStore.partnerCustomerInventoryTags;
        if (tags.length === 0) {
          setSelectedInventoryTag(undefined);
          return;
        }
        if (fromExisting && existingSwagpackId) {
          const existingSwagpack = (
            projectPackagingOpsStore.packagingRequestBody.swagpacks || []
          ).find((swagpack) => swagpack.swagpack_id === existingSwagpackId) as
            | any
            | undefined;

          const existingTagId =
            existingSwagpack?.partner_customer_inventory_tag_id ??
            existingSwagpack?.partnerCustomerInventoryTagId;
          const existingTagCode =
            existingSwagpack?.partner_customer_inventory_tag ??
            existingSwagpack?.partnerCustomerInventoryTag ??
            existingSwagpack?.tag_id;

          const matchedTag = tags.find(
            (tag) =>
              (existingTagId !== undefined &&
                String(tag.id) === String(existingTagId)) ||
              (!!existingTagCode &&
                (String(tag.tag_id) === String(existingTagCode) ||
                  String(tag.tag) === String(existingTagCode))),
          );

          if (matchedTag) {
            setSelectedInventoryTag({
              id: String(matchedTag.id),
              value: matchedTag.tag,
            });
            return;
          }
        }
        if (
          selectedInventoryTag?.id &&
          !tags.some((tag) => String(tag.id) === selectedInventoryTag?.id)
        ) {
          setSelectedInventoryTag(undefined);
        }
      })
      .catch(() => {
        showError("Error", "Failed to fetch customer inventory tags.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPackagingOpsStore.packagingRequestBody.customerId]);

  useEffect(() => {
    // Ensure data is loaded before initializing products
    setIsLoading(true);

    // Check if we have the required data, if not fetch it
    if (
      !projectPackagingOpsStore.packagingRequestBody.products ||
      projectPackagingOpsStore.packagingRequestBody.products?.length === 0 ||
      !projectPackagingOpsStore.packagingSourcingSwagpacks ||
      projectPackagingOpsStore.packagingSourcingSwagpacks?.length === 0
    ) {
      Promise.all([
        projectPackagingOpsStore.getProjectPackagingOpsDetails(projectId),
        projectPackagingOpsStore.getSourcingSwagpacks(projectId),
      ])
        .then(() => {
          // Now initialize products from URL parameters
          getProjectPackagingOpsProduct(projectId);
        })
        .catch((error) => {
          handleError(error, "Failed to load project data. Please try again.");
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Data is already available, initialize products
      getProjectPackagingOpsProduct(projectId);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Additional effect to re-initialize products when data becomes available
  useEffect(() => {
    if (
      projectPackagingOpsStore.packagingRequestBody.products &&
      projectPackagingOpsStore.packagingRequestBody.products?.length > 0 &&
      projectPackagingOpsStore.packagingSourcingSwagpacks &&
      projectPackagingOpsStore.packagingSourcingSwagpacks?.length > 0 &&
      filteredProducts?.length === 0 &&
      productIdsFromUrl?.length > 0
    ) {
      // Re-initialize products from URL parameters when data is available
      getProjectPackagingOpsProduct(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectPackagingOpsStore.packagingRequestBody.products,
    projectPackagingOpsStore.packagingSourcingSwagpacks,
    projectId,
    productIdsFromUrl,
  ]);

  // Effect to recalculate available quantities when store data changes
  useEffect(() => {
    if (
      projectPackagingOpsStore.packagingRequestBody.products &&
      projectPackagingOpsStore.packagingRequestBody.products?.length > 0 &&
      projectPackagingOpsStore.packagingSourcingSwagpacks &&
      projectPackagingOpsStore.packagingSourcingSwagpacks?.length > 0
    ) {
      calculateAvailableQuantities()
        .then((quantities) => {
          setAvailableQuantities(quantities);
        })
        .catch((error) =>
          handleError(
            error,
            "Failed to recalculate available quantities. Please try again.",
          ),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectPackagingOpsStore.packagingRequestBody.products,
    projectPackagingOpsStore.packagingSourcingSwagpacks,
  ]);

  const getProjectPackagingOpsProduct = (projectId: number) => {
    setIsLoading(true);

    const allProducts: ProductData[] =
      projectPackagingOpsStore.packagingRequestBody.products || [];
    const initialFilteredProducts = allProducts.filter((product) =>
      productIdsFromUrl?.includes(product.productId),
    );

    const initializeProducts = () => {
      const initialQuantities: { [key: number]: number } = {};
      const initialVariants: { [key: number]: string } = {};

      initialFilteredProducts.forEach((product) => {
        const { initialQuantity, initialVariant } =
          initializeProductData(product);
        initialQuantities[product.productId] = initialQuantity;
        if (initialVariant) {
          initialVariants[product.productId] = initialVariant;
        }
      });

      setQuantities(initialQuantities);
      setSelectedVariants(initialVariants);
      setFilteredProducts(initialFilteredProducts);
    };

    // Data should already be loaded from the main page
    if (allProducts?.length > 0) {
      initializeProducts();
      setIsLoading(false);
    } else {
      // Fallback: if data is not available, show error
      handleError(
        new Error("Project data not available"),
        "Please return to the main page and try again.",
      );
      setIsLoading(false);
    }
  };

  const handleCreatePack = () => {
    setIsLoading(true);

    // Refresh available quantities
    calculateAvailableQuantities()
      .then((quantities) => {
        setAvailableQuantities(quantities);
      })
      .catch((error) => {
        handleError(
          error,
          "Failed to fetch available quantities. Please try again.",
        );
      });

    // Basic validation
    if (!packName.trim()) {
      showError("Validation Error", "Please enter a pack name");
      setIsLoading(false);
      return;
    }

    if (
      partnerCustomerInventoryTagsStore.partnerCustomerInventoryTags.length >
      0 &&
      !selectedInventoryTag?.id
    ) {
      showError("Validation Error", "Please select a customer inventory tag");
      setIsLoading(false);
      return;
    }

    const effectiveTotalPacksQuantity = calculateBulkTotalQuantity();

    if (!fromExisting) {
      if (effectiveTotalPacksQuantity <= 0) {
        showError(
          "Validation Error",
          "Total packs quantity must be greater than 0",
        );
        setIsLoading(false);
        return;
      }

      if (filteredProducts?.length === 0) {
        showError(
          "Validation Error",
          "Please add at least one product to the pack",
        );
        setIsLoading(false);
        return;
      }
    } else {
      if (filteredProducts?.length === 0) {
        showError(
          "Validation Error",
          "Please add at least one product to add quantity",
        );
        setIsLoading(false);
        return;
      }
    }

    // Validate all products
    for (const product of filteredProducts) {
      const prId = product.productId;

      if (!validateProductVariant(product, prId)) {
        setIsLoading(false);
        return;
      }

      if (!fromExisting && !validateProductQuantity(product, prId)) {
        setIsLoading(false);
        return;
      }

      if (
        (!fromExisting || effectiveTotalPacksQuantity > 0) &&
        !validateStockAvailability(product, prId, effectiveTotalPacksQuantity)
      ) {
        setIsLoading(false);
        return;
      }

      if (!validateVariantSections(product, prId)) {
        setIsLoading(false);
        return;
      }

      const mainStateKey = getVariantStateKey(prId);
      const mainPackSize = Number(variantPackSizes[mainStateKey] || 1);
      if (!Number.isInteger(mainPackSize) || mainPackSize < 1) {
        showError(
          "Validation Error",
          `Pack size for ${product.name} must be a positive integer`,
        );
        setIsLoading(false);
        return;
      }
    }

    // Build products array
    const products = [];

    for (const product of filteredProducts) {
      const prId = product.productId;
      const productName = product.name;
      const colors = product.options?.Color || [];

      const productObj = {
        id: prId,
        name: productName,
        quantity: fromExisting && effectiveTotalPacksQuantity === 0 ? 0 : 1,
        variants: [] as Array<{
          variant_quantity: number;
          size: string;
          pack_size: number;
          box_size: "S" | "M" | "L" | "XL" | "SP" | undefined;
          color?: string;
          client_sku?: string;
        }>,
      };

      // Handle main variant
      const mainVariantQty = getVariantQuantity(
        product,
        prId,
        undefined,
        effectiveTotalPacksQuantity,
      );
      const { sizeName: mainVariantSizeName } = getVariantSizeInfo(
        product,
        prId,
        undefined,
        effectiveTotalPacksQuantity,
      );
      const mainSku = projectPackagingOpsStore.skus?.[prId] || "";
      const mainStateKey = getVariantStateKey(prId);
      const mainPackSize = Number(variantPackSizes[mainStateKey] || 1);
      const mainClassification =
        variantClassifications[mainStateKey] || undefined;

      if (mainVariantQty > 0 && mainVariantSizeName) {
        if (!Number.isInteger(mainPackSize) || mainPackSize < 1) {
          showError(
            "Validation Error",
            `Pack size for ${productName} must be a positive integer`,
          );
          setIsLoading(false);
          return;
        }
        const variant = createVariantObject(
          mainVariantQty,
          mainVariantSizeName,
          mainPackSize,
          mainClassification?.id,
          colors,
          mainSku,
        );
        productObj.variants.push(variant);
      }

      // Handle additional variants
      if (variantSections[prId]) {
        for (const variantKey of variantSections[prId]) {
          const variantQty = getVariantQuantity(
            product,
            prId,
            variantKey,
            effectiveTotalPacksQuantity,
          );
          const { sizeName: variantSizeName } = getVariantSizeInfo(
            product,
            prId,
            variantKey,
            effectiveTotalPacksQuantity,
          );
          const variantSku =
            projectPackagingOpsStore.skus?.[`${prId}_${variantKey}`] || "";
          const variantStateKey = getVariantStateKey(prId, variantKey);
          const variantPackSize = Number(
            variantPackSizes[variantStateKey] || 1,
          );
          const variantClassification =
            variantClassifications[variantStateKey] || undefined;

          if (variantQty > 0 && variantSizeName) {
            if (!Number.isInteger(variantPackSize) || variantPackSize < 1) {
              showError(
                "Validation Error",
                `Pack size for each variant of ${productName} must be a positive integer`,
              );
              setIsLoading(false);
              return;
            }
            const variant = createVariantObject(
              variantQty,
              variantSizeName,
              variantPackSize,
              variantClassification?.id,
              colors,
              variantSku,
            );
            productObj.variants.push(variant);
          }
        }
      }

      if (!fromExisting && productObj.variants?.length === 0) {
        showError(
          "Validation Error",
          `Please add at least one variant with quantity > 0 for ${productName}`,
        );
        return;
      }

      products.push(productObj);
    }

    // Create pack
    const packData = createPackData(products, effectiveTotalPacksQuantity);
    handleSwagPackCreation(packData, fromExisting);
  };

  // Helper functions for UI interactions
  const handleChange = (productId: number, delta: number) => {
    setQuantities((prev) => ({
      ...prev,
      [productId]: Math.max(
        fromExisting ? 0 : 1,
        (prev[productId] || (fromExisting ? 0 : 1)) + delta,
      ),
    }));
  };

  const handleVariantChange = (productId: number, variantId: string) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [productId]: variantId,
    }));

    if (variantId) {
      projectPackagingOpsStore.generateSku(productId, variantId);
    }
  };

  const handleSkuChange = (key: string, value: string) => {
    projectPackagingOpsStore.setSku(key, value);
  };

  const validateMinimumProducts = (productIds: number[]) => {
    const minRequired = popupMode === "pack" ? 2 : 1;
    if (productIds?.length < minRequired) {
      showError(
        "Validation Error",
        `At least ${minRequired} product${minRequired > 1 ? "s" : ""} ${popupMode === "pack" ? "are" : "is"
        } required to proceed in ${popupMode} mode.`,
      );
      return false;
    }
    return true;
  };

  const clearProductData = (productId: number) => {
    setVariantSections((prev) => {
      const newSections = { ...prev };
      delete newSections[productId];
      return newSections;
    });

    setQuantities((prev) => {
      const newQuantities = { ...prev };
      Object.keys(newQuantities).forEach((key) => {
        if (key.startsWith(`${productId}`) || key === productId.toString()) {
          delete newQuantities[key];
        }
      });
      return newQuantities;
    });

    setSelectedVariants((prev) => {
      const newVariants = { ...prev };
      Object.keys(newVariants).forEach((key) => {
        if (key.startsWith(`${productId}`) || key === productId.toString()) {
          delete newVariants[key];
        }
      });
      return newVariants;
    });

    setVariantPackSizes((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${productId}_`)) {
          delete next[key];
        }
      });
      return next;
    });

    setVariantClassifications((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${productId}_`)) {
          delete next[key];
        }
      });
      return next;
    });

    projectPackagingOpsStore.clearSkuForProduct(productId);
  };

  const updateUrlWithProductIds = (productIds: number[]) => {
    const queryParams = new URLSearchParams();
    queryParams.append("project-id", String(projectId));
    if (productIds?.length > 0) {
      queryParams.append("productId", productIds.join(","));
    }
    navigate(`/projects/create-pack?${queryParams.toString()}`);
  };

  const handleDeleteProduct = (productId: number) => {
    const updatedProductIds = productIdsFromUrl.filter(
      (id) => id !== productId,
    );

    if (!validateMinimumProducts(updatedProductIds)) {
      return;
    }

    updateUrlWithProductIds(updatedProductIds);

    const updatedProducts = filteredProducts.filter(
      (product) => product.productId !== productId,
    );
    setFilteredProducts(updatedProducts);
    clearProductData(productId);
    setProductPopupOpen(false);
  };

  const handleAddProduct = (selectedProducts: number[]) => {
    const updatedProductIds = Array.from(new Set(selectedProducts.map(Number)));

    if (!validateMinimumProducts(updatedProductIds)) {
      return;
    }

    const removedProductIds = productIdsFromUrl.filter(
      (id) => !updatedProductIds.includes(id),
    );

    removedProductIds.forEach(clearProductData);
    updateUrlWithProductIds(updatedProductIds);

    const allProducts: ProductData[] =
      projectPackagingOpsStore.packagingRequestBody.products || [];

    const newProducts = allProducts.filter((product) =>
      updatedProductIds.includes(product.productId),
    );

    if (
      popupMode === "bulk" &&
      newProducts?.length === 1 &&
      !projectPackagingOpsStore.isPackNameManuallyEdited
    ) {
      setPackName(newProducts[0].name);
    }

    const newQuantities = { ...quantities };
    const newPackSizes = { ...variantPackSizes };
    const newClassifications = { ...variantClassifications };
    newProducts.forEach((product) => {
      if (!newQuantities[product.productId]) {
        newQuantities[product.productId] = fromExisting ? 0 : 1;
      }
      const mainKey = getVariantStateKey(product.productId);
      if (!newPackSizes[mainKey]) {
        newPackSizes[mainKey] = 1;
      }
      if (!newClassifications[mainKey]) {
        newClassifications[mainKey] = undefined;
      }
      if (product.options?.Size?.length === 1 && !fromExisting) {
        const sizeId = product.options.Size[0].id.toString();
        projectPackagingOpsStore.generateSku(product.productId, sizeId);
      } else if (product.options?.Size?.length > 1) {
        projectPackagingOpsStore.clearSkuForProduct(product.productId);
      }
    });
    setQuantities(newQuantities);
    setVariantPackSizes(newPackSizes);
    setVariantClassifications(newClassifications);
    setFilteredProducts(newProducts);
    setProductPopupOpen(false);
  };

  const handleCancelProduct = () => {
    setProductPopupOpen(false);
  };

  const handleClearAll = useCallback(() => { }, []);

  const handleAddVariant = (productId: number) => {
    const uniqueKey = Date.now();
    setVariantSections((prev) => ({
      ...prev,
      [productId]: [...(prev[productId] || []), uniqueKey],
    }));

    setQuantities((prev) => ({
      ...prev,
      [`${productId}_${uniqueKey}`]: fromExisting ? 0 : 1,
    }));
    const variantStateKey = getVariantStateKey(productId, uniqueKey);
    setVariantPackSizes((prev) => ({ ...prev, [variantStateKey]: 1 }));
    setVariantClassifications((prev) => ({
      ...prev,
      [variantStateKey]: undefined,
    }));

    const product = filteredProducts.find((p) => p.productId === productId);
    if (product?.options?.Size?.length === 1) {
      const sizeId = product.options.Size[0].id.toString();
      setSelectedVariants((prev) => ({
        ...prev,
        [`${productId}_${uniqueKey}`]: sizeId,
      }));
      if (!fromExisting) {
        projectPackagingOpsStore.generateSku(
          productId,
          sizeId,
          `${productId}_${uniqueKey}`,
        );
      }
    }
  };

  const handleRemoveVariant = (productId: number, variantKey: number) => {
    setVariantSections((prev) => ({
      ...prev,
      [productId]: prev[productId].filter((key) => key !== variantKey),
    }));

    setQuantities((prev) => {
      const newQuantities = { ...prev };
      delete newQuantities[`${productId}_${variantKey}`];
      return newQuantities;
    });

    setSelectedVariants((prev) => {
      const newVariants = { ...prev };
      delete newVariants[`${productId}_${variantKey}`];
      return newVariants;
    });

    const stateKey = getVariantStateKey(productId, variantKey);
    setVariantPackSizes((prev) => {
      const next = { ...prev };
      delete next[stateKey];
      return next;
    });
    setVariantClassifications((prev) => {
      const next = { ...prev };
      delete next[stateKey];
      return next;
    });

    projectPackagingOpsStore.clearSkuForProduct(
      productId,
      `${productId}_${variantKey}`,
    );
  };

  const isMultipleProducts = filteredProducts?.length > 1;

  const calculateBulkTotalQuantity = () => {
    if (popupMode !== "bulk" || filteredProducts?.length !== 1) {
      return Number(totalPacksQuantity || 0);
    }

    const product = filteredProducts[0];
    const productId = product.productId;
    let total = quantities[productId] || 0;

    if (variantSections[productId]) {
      for (const variantKey of variantSections[productId]) {
        total += quantities[`${productId}_${variantKey}`] || 0;
      }
    }

    return total;
  };

  useEffect(() => {
    setIsLoading(true);

    // Ensure data is loaded before filtering
    if (
      !projectPackagingOpsStore.packagingRequestBody.products ||
      projectPackagingOpsStore.packagingRequestBody.products?.length === 0 ||
      !projectPackagingOpsStore.packagingSourcingSwagpacks ||
      projectPackagingOpsStore.packagingSourcingSwagpacks?.length === 0
    ) {
      Promise.all([
        projectPackagingOpsStore.getProjectPackagingOpsDetails(projectId),
        projectPackagingOpsStore.getSourcingSwagpacks(projectId),
      ])
        .then(() => {
          // Now get filtered products
          const result = projectPackagingOpsStore.filterProductsForPopup();
          setFilteredProductsForPopup(result);
        })
        .catch((error) => {
          handleError(
            error,
            "Failed to load products for selection. Please try again.",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Data is already available, get filtered products
      const result = projectPackagingOpsStore.filterProductsForPopup();
      setFilteredProductsForPopup(result);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (
      popupMode === "bulk" &&
      filteredProducts?.length === 1 &&
      !projectPackagingOpsStore.isPackNameManuallyEdited &&
      !fromExisting
    ) {
      setPackName(filteredProducts[0].name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredProducts,
    popupMode,
    projectPackagingOpsStore.isPackNameManuallyEdited,
    fromExisting,
  ]);

  useEffect(() => {
    if (
      fromExisting &&
      projectPackagingOpsStore.packagingRequestBody.swagpacks &&
      !projectPackagingOpsStore.isPackNameManuallyEdited
    ) {
      const existingSwagpack =
        projectPackagingOpsStore.packagingRequestBody.swagpacks.find(
          (swagpack) => swagpack.swagpack_id === existingSwagpackId,
        );
      if (existingSwagpack && !packName) {
        setPackName(existingSwagpack.swagpack_name);
      }
    }
  }, [
    fromExisting,
    existingSwagpackId,
    projectPackagingOpsStore.packagingRequestBody.swagpacks,
    packName,
    popupMode,
    projectPackagingOpsStore.isPackNameManuallyEdited,
  ]);

  useEffect(() => {
    if (
      fromExisting &&
      projectPackagingOpsStore.packagingRequestBody.swagpacks &&
      filteredProducts?.length > 0
    ) {
      const existingSwagpack =
        projectPackagingOpsStore.packagingRequestBody.swagpacks.find(
          (swagpack) => swagpack.swagpack_id === existingSwagpackId,
        );

      if (existingSwagpack) {
        const initialQuantities: { [key: number]: number } = {};
        const initialVariants: { [key: number]: string } = {};
        const initialPackSizes: { [key: string]: number } = {};
        const initialClassifications: {
          [key: string]: FilterValue | undefined;
        } = {};

        // For existing packs, default total pack quantity to 0 (user needs to manually set it)
        if (popupMode !== "bulk") {
          setTotalPacksQuantity(0);
        }

        existingSwagpack.products.forEach((swagpackProduct) => {
          const productId = swagpackProduct.product_id;
          const product = filteredProducts.find(
            (p) => p.productId === productId,
          );

          if (product && swagpackProduct.variants?.length > 0) {
            const firstVariant = swagpackProduct.variants[0];
            // For existing packs, set quantities based on mode
            if (fromExisting && popupMode === "bulk") {
              // For bulk mode, start with 0 quantities
              initialQuantities[productId] = 0;
            } else {
              // For pack mode, use existing quantities
              initialQuantities[productId] = firstVariant.quantity || 1;
            }
            initialPackSizes[getVariantStateKey(productId)] =
              Number((firstVariant as any)?.pack_size) > 0
                ? Number((firstVariant as any).pack_size)
                : 1;

            const firstVariantBoxSize =
              (firstVariant as any)?.classification ||
              (firstVariant as any)?.box_size ||
              "SP";
            initialClassifications[getVariantStateKey(productId)] =
              resolveClassificationOption(firstVariantBoxSize) ||
              PACK_CLASSIFICATION_OPTIONS.find((option) => option.id === "SP");

            // Always prefill SKU from existing swagpack variants in edit flow.
            if (fromExisting && firstVariant.client_sku) {
              projectPackagingOpsStore.setSku(
                productId.toString(),
                firstVariant.client_sku,
              );
            }

            // Pre-select variant for pack mode, but not for bulk mode
            if (
              product.options?.Size &&
              !(fromExisting && popupMode === "bulk")
            ) {
              const matchingSize = product.options.Size.find(
                (size) =>
                  size.name.toLowerCase() === firstVariant.size.toLowerCase(),
              );
              if (matchingSize) {
                const sizeId = matchingSize.id.toString();
                initialVariants[productId] = sizeId;

                if (fromExisting) {
                  // Handle additional variants if they exist
                  if (swagpackProduct.variants?.length > 1) {
                    swagpackProduct.variants
                      .slice(1)
                      .forEach((variant, index) => {
                        const variantKey = Date.now() + index; // Generate a unique key for additional variants
                        if (variant.client_sku) {
                          projectPackagingOpsStore.setSku(
                            `${productId}_${variantKey}`,
                            variant.client_sku,
                          );
                        }
                        const stateKey = getVariantStateKey(
                          productId,
                          variantKey,
                        );
                        initialPackSizes[stateKey] =
                          Number((variant as any)?.pack_size) > 0
                            ? Number((variant as any).pack_size)
                            : 1;
                        const variantBoxSize =
                          (variant as any)?.classification ||
                          (variant as any)?.box_size ||
                          "SP";
                        initialClassifications[stateKey] =
                          resolveClassificationOption(variantBoxSize) ||
                          PACK_CLASSIFICATION_OPTIONS.find(
                            (option) => option.id === "SP",
                          );
                      });
                  }
                } else {
                  // For new packs, generate SKU
                  projectPackagingOpsStore.generateSku(productId, sizeId);
                }
              }
            }
          }
        });

        if (fromExisting && popupMode === "bulk") {
          setVariantSections({});
          setSelectedVariants((prev) => ({ ...prev, ...initialVariants }));
          setVariantPackSizes((prev) => ({ ...prev, ...initialPackSizes }));
          setVariantClassifications((prev) => ({
            ...prev,
            ...initialClassifications,
          }));
          setQuantities((prev) => {
            const newQuantities: { [key: string]: number } = {};
            Object.keys(prev).forEach((key) => {
              newQuantities[key] = 0;
            });
            filteredProducts.forEach((product) => {
              newQuantities[product.productId] = 0;
            });
            return newQuantities;
          });
        } else {
          // For pack mode, use existing quantities and variants
          setQuantities((prev) => ({ ...prev, ...initialQuantities }));
          setSelectedVariants((prev) => ({ ...prev, ...initialVariants }));
          setVariantPackSizes((prev) => ({ ...prev, ...initialPackSizes }));
          setVariantClassifications((prev) => ({
            ...prev,
            ...initialClassifications,
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fromExisting,
    existingSwagpackId,
    projectPackagingOpsStore.packagingRequestBody.swagpacks,
    filteredProducts,
    popupMode,
  ]);

  const modeDisplay = popupMode === "bulk" ? "Bulk" : "Swag Pack";
  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container
        maxWidth="xl"
        sx={{
          pl: { xl: "6.25rem", lg: "4.063rem" },
          pr: { xl: "6.25rem", lg: "4.063rem" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            color: theme.palette.customColors.greyTextColor,
            mb: 2,
            mt: 2,
            maxWidth: "3.75rem",
            "&:hover, &:active": {
              color: theme.palette.customColors.greyTextColor,
            },
          }}
          onClick={() => {
            navigate(`/projects/${projectId}?tab_index=3`);
          }}
        >
          <ArrowBackIcon sx={{ fontSize: "1rem" }} />
          <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>Back</span>
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 5,
            }}
          >
            <Box>
              <CustomerHeading
                label={
                  projectPackagingOpsStore.packagingRequestBody?.label || ""
                }
                orderId={
                  projectPackagingOpsStore.packagingRequestBody.project_id || 0
                }
                moduleName={
                  fromExisting
                    ? isMultipleProducts
                      ? "Add Quantity to Pack"
                      : "Add Quantity to Bulk"
                    : isMultipleProducts
                      ? "Create Pack"
                      : "Create Bulk"
                }
                brandName={
                  projectPackagingOpsStore.packagingRequestBody.companyName ||
                  ""
                }
              />
            </Box>
          </Box>
          <Divider sx={{ mt: 2, mb: 3 }} />

          <Box>
            <Card sx={{ p: 5 }}>
              <CustomTypography variant="heading5">
                Pack Information
              </CustomTypography>

              <Box
                sx={{
                  mt: 2,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <CustomTypography
                  variant="heading7"
                  color={theme.palette.customColors.greyTextColor}
                >
                  Pack Name
                </CustomTypography>
                {Boolean(projectPackagingOpsStore.packagingRequestBody?.is_master) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.greyTextColor}
                    >
                      Create New Pack
                    </CustomTypography>
                    <CustomToggleSwitch
                      checked={createPackWithExistingId}
                      onChange={(e) =>
                        setCreatePackWithExistingId(e.target.checked)
                      }
                      activeColor={theme.palette.customColors.purple}
                      inactiveColor={
                        theme.palette.customColors.switchTrackGray || "#ccc"
                      }
                    />
                  </Box>
                )}
              </Box>

              <CustomTextField
                borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
                value={packName}
                placeholder="Enter Pack Name..."
                width="100%"
                type="text"
                onChange={(e) => {
                  setPackName(e.target.value);
                  projectPackagingOpsStore.setPackNameManuallyEdited(true);
                }}
              />

              {partnerCustomerInventoryTagsStore.partnerCustomerInventoryTags
                .length > 0 && (
                  <>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.greyTextColor}
                      style={{ marginBottom: 10, marginTop: 15 }}
                    >
                      Customer Inventory Tags
                    </CustomTypography>

                    <AppSearchDropdownMulti
                      borderColor={theme.palette.customColors.blackBorder}
                      isMultiSelect={false}
                      title="Select Delivery Partner"
                      fontSize="0.8rem"
                      value={
                        selectedInventoryTag ? [selectedInventoryTag] : undefined
                      }
                      list={partnerCustomerInventoryTagsStore.partnerCustomerInventoryTags.map(
                        (tag) => ({
                          id: String(tag.id),
                          value: tag.tag,
                        }),
                      )}
                      local={true}
                      handleSelect={(selectedItems) => {
                        if (selectedItems.length === 0) {
                          setSelectedInventoryTag(undefined);
                        } else {
                          setSelectedInventoryTag(selectedItems[0]);
                        }
                      }}
                    />
                  </>
                )}

              {popupMode !== "bulk" && (
                <>
                  <CustomTypography
                    variant="heading7"
                    color={theme.palette.customColors.greyTextColor}
                    style={{ marginBottom: 10, marginTop: 15 }}
                  >
                    {fromExisting ? "Quantity to Add" : "Total Packs Quantity"}
                  </CustomTypography>

                  <CustomTextField
                    allowNegativeValue={false}
                    allowDecimalValue={false}
                    borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
                    value={totalPacksQuantity}
                    placeholder="00"
                    width="100%"
                    type="number"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        setTotalPacksQuantity("");
                      } else {
                        const numericValue = parseInt(value, 10);
                        if (!isNaN(numericValue) && numericValue >= 0) {
                          setTotalPacksQuantity(numericValue);
                        }
                      }
                    }}
                  />
                </>
              )}
            </Card>
          </Box>

          <Card sx={{ p: 5, mt: 3 }}>
            {!fromExisting && (
              <CustomTypography variant="heading5">
                Add Products
              </CustomTypography>
            )}

            {!fromExisting && (
              <Box sx={{ textAlign: "right", marginBottom: 3 }}>
                <AppButton
                  label="Browse Products"
                  customStyles={{
                    height: "2.463rem",
                    minWidth: "9.438rem",
                    backgroundColor: theme.palette.customColors.blackBorder,
                    color: theme.palette.customColors.white,
                  }}
                  variant="grayscaleBlock"
                  onClick={() => {
                    setIsLoading(true);

                    // Ensure data is loaded before opening popup
                    if (
                      !projectPackagingOpsStore.packagingRequestBody.products ||
                      projectPackagingOpsStore.packagingRequestBody.products
                        ?.length === 0 ||
                      !projectPackagingOpsStore.packagingSourcingSwagpacks ||
                      projectPackagingOpsStore.packagingSourcingSwagpacks
                        ?.length === 0
                    ) {
                      Promise.all([
                        projectPackagingOpsStore.getProjectPackagingOpsDetails(
                          projectId,
                        ),
                        projectPackagingOpsStore.getSourcingSwagpacks(
                          projectId,
                        ),
                      ])
                        .then(() => {
                          const result =
                            projectPackagingOpsStore.filterProductsForPopup();
                          setFilteredProductsForPopup(result);
                          setProductPopupOpen(true);
                        })
                        .catch((error) => {
                          handleError(
                            error,
                            "Failed to load products for selection. Please try again.",
                          );
                        })
                        .finally(() => {
                          setIsLoading(false);
                        });
                    } else {
                      const result =
                        projectPackagingOpsStore.filterProductsForPopup();
                      setFilteredProductsForPopup(result);
                      setProductPopupOpen(true);
                      setIsLoading(false);
                    }
                  }}
                />
              </Box>
            )}

            {filteredProducts?.length > 0 ? (
              filteredProducts.map((product) => (
                <Card key={product.productId} sx={{ mb: 2 }}>
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      padding: "0 !important",
                      m: 2,
                      gap: 4,
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      alignItems="flex-start" // always flex-start
                      gap={2}
                      flex={1}
                      width="100%"
                    >
                      <Box
                        sx={{
                          height: "80px",
                          width: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor:
                            theme.palette.customColors.grayBackGroundColor,
                          borderRadius: 1,
                        }}
                      >
                        <img
                          style={{
                            height: "70%",
                            width: "70%",
                            objectFit: "contain",
                          }}
                          src={product.image_url || "default-image-path"}
                          alt={product.name}
                        />
                      </Box>

                      <Box width="100%">
                        <CustomTypography
                          variant="heading6"
                          style={{ paddingBottom: 8 }}
                        >
                          {product.name}
                        </CustomTypography>

                        {product.options?.Size?.length > 0 && (
                          <>
                            <Box
                              display="flex"
                              flexWrap="wrap"
                              alignItems="flex-start"
                              gap={1}
                              mt={2}
                            >
                              <Box
                                sx={{
                                  minWidth: "150px",
                                  maxWidth: "150px",
                                  flexGrow: 1,
                                }}
                              >
                                <CustomTypography
                                  variant="body2"
                                  color={
                                    theme.palette.customColors.greyTextColor
                                  }
                                  style={{ marginBottom: 8, fontWeight: 600 }}
                                >
                                  Size
                                </CustomTypography>
                                <AppSearchDropdownMulti
                                  borderColor={
                                    theme.palette.customColors.blackBorder
                                  }
                                  isMultiSelect={false}
                                  title="Select Size"
                                  fontSize="0.8rem"
                                  value={(() => {
                                    const selectedSizeId =
                                      product.options.Size.length === 1
                                        ? product.options.Size[0].id.toString()
                                        : selectedVariants[product.productId];
                                    const selectedSize = product.options.Size.find(
                                      (variant) =>
                                        variant.id.toString() === selectedSizeId,
                                    );
                                    return selectedSize
                                      ? [
                                        {
                                          id: selectedSize.id.toString(),
                                          value: selectedSize.name,
                                        },
                                      ]
                                      : undefined;
                                  })()}
                                  list={product.options.Size.map((variant) => ({
                                    id: variant.id.toString(),
                                    value: variant.name,
                                  }))}
                                  local={true}
                                  handleSelect={(selectedItems) => {
                                    if (selectedItems.length > 0) {
                                      handleVariantChange(
                                        product.productId,
                                        selectedItems[0].id,
                                      );
                                    }
                                  }}
                                />
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                  flexDirection: "column",
                                  minWidth: "170px",
                                }}
                              >
                                <CustomTypography
                                  variant="body2"
                                  color={
                                    theme.palette.customColors.greyTextColor
                                  }
                                  style={{
                                    fontWeight: 600,
                                    alignSelf: "flex-start",
                                  }}
                                >
                                  Quantity
                                </CustomTypography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  <IconButton
                                    onClick={() =>
                                      handleChange(product.productId, -1)
                                    }
                                    disabled={
                                      fromExisting && popupMode === "pack"
                                    }
                                    sx={{
                                      border: `1px solid ${theme.palette.customColors.greyDarkborder}`,
                                      borderRadius: "4px",
                                      backgroundColor:
                                        theme.palette.customColors
                                          .grayBackGroundColor,
                                    }}
                                  >
                                    <RemoveIcon sx={{ fontSize: "14px" }} />
                                  </IconButton>
                                  <CustomTextField
                                    borderRadius={1}
                                    allowDecimalValue={false}
                                    allowNegativeValue={false}
                                    borderColor={`1px solid ${theme.palette.customColors.greyDarkborder}`}
                                    value={
                                      fromExisting && popupMode === "bulk"
                                        ? quantities[product.productId] === 0
                                          ? ""
                                          : quantities[
                                            product.productId
                                          ]?.toString() || ""
                                        : quantities[
                                          product.productId
                                        ]?.toString() || ""
                                    }
                                    width="70px"
                                    type="number"
                                    placeholder="0"
                                    disabled={
                                      fromExisting && popupMode === "pack"
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value;

                                      if (value === "") {
                                        setQuantities((prev) => ({
                                          ...prev,
                                          [product.productId]: 0,
                                        }));
                                      } else {
                                        const numericValue = parseInt(
                                          value,
                                          10,
                                        );
                                        if (
                                          !isNaN(numericValue) &&
                                          numericValue >= 0
                                        ) {
                                          setQuantities((prev) => ({
                                            ...prev,
                                            [product.productId]: numericValue,
                                          }));
                                        }
                                      }
                                    }}
                                  />

                                  <IconButton
                                    onClick={() =>
                                      handleChange(product.productId, 1)
                                    }
                                    disabled={
                                      fromExisting && popupMode === "pack"
                                    }
                                    sx={{
                                      border: `1px solid ${theme.palette.customColors.greyDarkborder}`,
                                      borderRadius: "4px",
                                      backgroundColor:
                                        theme.palette.customColors
                                          .grayBackGroundColor,
                                    }}
                                  >
                                    <AddIcon sx={{ fontSize: "14px" }} />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  minWidth: "150px",
                                  maxWidth: "150px",
                                  flexGrow: 1,
                                }}
                              >
                                <CustomTypography
                                  variant="body2"
                                  color={
                                    theme.palette.customColors.greyTextColor
                                  }
                                  style={{ marginBottom: 8, fontWeight: 600 }}
                                >
                                  Pack Size
                                </CustomTypography>
                                <CustomTextField
                                  allowDecimalValue={false}
                                  allowNegativeValue={false}
                                  borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
                                  borderRadius={1}
                                  value={(
                                    variantPackSizes[
                                    getVariantStateKey(product.productId)
                                    ] || 1
                                  ).toString()}
                                  placeholder="Pack Size"
                                  width="100%"
                                  type="number"
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    const nextValue =
                                      !isNaN(value) && value > 0 ? value : 1;
                                    setVariantPackSizes((prev) => ({
                                      ...prev,
                                      [getVariantStateKey(product.productId)]:
                                        nextValue,
                                    }));
                                  }}
                                  disabled={
                                    fromExisting && popupMode === "pack"
                                  }
                                />
                              </Box>

                              <Box
                                sx={{
                                  minWidth: "170px",
                                  maxWidth: "170px",
                                  flexGrow: 1,
                                }}
                              >
                                <CustomTypography
                                  variant="body2"
                                  color={
                                    theme.palette.customColors.greyTextColor
                                  }
                                  style={{ marginBottom: 8, fontWeight: 600 }}
                                >
                                  Classification
                                </CustomTypography>
                                <AppSearchDropdownMulti
                                  borderColor={
                                    theme.palette.customColors.blackBorder
                                  }
                                  isMultiSelect={false}
                                  title="Select Classification"
                                  fontSize="0.8rem"
                                  value={getClassificationSelection(
                                    variantClassifications[
                                    getVariantStateKey(product.productId)
                                    ] as FilterValue | string | undefined,
                                  )}
                                  list={PACK_CLASSIFICATION_OPTIONS}
                                  local={true}
                                  handleSelect={(selectedItems) => {
                                    setVariantClassifications((prev) => ({
                                      ...prev,
                                      [getVariantStateKey(product.productId)]:
                                        selectedItems[0],
                                    }));
                                  }}
                                />
                              </Box>

                              <Box
                                sx={{
                                  minWidth: "150px",
                                  maxWidth: "150px",
                                  flexGrow: 1,
                                }}
                              >
                                <CustomTypography
                                  variant="body2"
                                  color={
                                    theme.palette.customColors.greyTextColor
                                  }
                                  style={{ marginBottom: 8, fontWeight: 600 }}
                                >
                                  SKU
                                </CustomTypography>
                                <CustomTextField
                                  borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
                                  borderRadius={1}
                                  value={
                                    projectPackagingOpsStore.skus?.[
                                    product.productId
                                    ] || ""
                                  }
                                  placeholder={"SECI-II-PI-Size"}
                                  width="100%"
                                  type="text"
                                  disabled={
                                    fromExisting && popupMode === "pack"
                                  }
                                  onChange={(e) =>
                                    handleSkuChange(
                                      product.productId.toString(),
                                      e.target.value,
                                    )
                                  }
                                />
                              </Box>
                            </Box>

                            {variantSections[product.productId]?.map(
                              (variantKey) => (
                                <Box
                                  key={variantKey}
                                  display="flex"
                                  flexWrap="wrap"
                                  alignItems="flex-start"
                                  gap={1}
                                  mt={2}
                                >
                                  <Box
                                    sx={{
                                      minWidth: "150px",
                                      maxWidth: "150px",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body2"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                      style={{
                                        marginBottom: 8,
                                        fontWeight: 600,
                                      }}
                                    >
                                      Size
                                    </CustomTypography>
                                    <AppSearchDropdownMulti
                                      borderColor={
                                        theme.palette.customColors.blackBorder
                                      }
                                      isMultiSelect={false}
                                      title="Select Size"
                                      fontSize="0.8rem"
                                      value={(() => {
                                        const selectedSizeId =
                                          product.options.Size.length === 1
                                            ? product.options.Size[0].id.toString()
                                            : selectedVariants[
                                            `${product.productId}_${variantKey}`
                                            ];
                                        const selectedSize =
                                          product.options.Size.find(
                                            (variant) =>
                                              variant.id.toString() ===
                                              selectedSizeId,
                                          );
                                        return selectedSize
                                          ? [
                                            {
                                              id: selectedSize.id.toString(),
                                              value: selectedSize.name,
                                            },
                                          ]
                                          : undefined;
                                      })()}
                                      list={product.options.Size.map(
                                        (variant) => ({
                                          id: variant.id.toString(),
                                          value: variant.name,
                                        }),
                                      )}
                                      local={true}
                                      handleSelect={(selectedItems) => {
                                        if (selectedItems.length > 0) {
                                          const sizeId = selectedItems[0].id;
                                          setSelectedVariants((prev) => ({
                                            ...prev,
                                            [`${product.productId}_${variantKey}`]:
                                              sizeId,
                                          }));
                                          if (sizeId) {
                                            projectPackagingOpsStore.generateSku(
                                              product.productId,
                                              sizeId,
                                              `${product.productId}_${variantKey}`,
                                            );
                                          }
                                        }
                                      }}
                                    />
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                      flexDirection: "column",
                                      minWidth: "170px",
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body2"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                      style={{
                                        fontWeight: 600,
                                        alignSelf: "flex-start",
                                      }}
                                    >
                                      Quantity
                                    </CustomTypography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        alignItems: "center",
                                      }}
                                    >
                                      <IconButton
                                        onClick={() =>
                                          setQuantities((prev) => ({
                                            ...prev,
                                            [`${product.productId}_${variantKey}`]:
                                              Math.max(
                                                fromExisting ? 0 : 1,
                                                (prev[
                                                  `${product.productId}_${variantKey}`
                                                ] || (fromExisting ? 0 : 1)) -
                                                1,
                                              ),
                                          }))
                                        }
                                        disabled={
                                          fromExisting && popupMode === "pack"
                                        }
                                        sx={{
                                          border: `1px solid ${theme.palette.customColors.greyDarkborder}`,
                                          borderRadius: "4px",
                                          backgroundColor:
                                            theme.palette.customColors
                                              .grayBackGroundColor,
                                        }}
                                      >
                                        <RemoveIcon sx={{ fontSize: "14px" }} />
                                      </IconButton>

                                      <CustomTextField
                                        allowDecimalValue={false}
                                        allowNegativeValue={false}
                                        borderColor={`1px solid ${theme.palette.customColors.greyDarkborder}`}
                                        width="70px"
                                        type="number"
                                        placeholder="0"
                                        disabled={
                                          fromExisting && popupMode === "pack"
                                        }
                                        value={
                                          fromExisting && popupMode === "bulk"
                                            ? quantities[
                                              `${product.productId}_${variantKey}`
                                            ] === 0
                                              ? ""
                                              : quantities[
                                                `${product.productId}_${variantKey}`
                                              ]?.toString() || ""
                                            : quantities[
                                              `${product.productId}_${variantKey}`
                                            ]?.toString() || ""
                                        }
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          const key = `${product.productId}_${variantKey}`;

                                          if (value === "") {
                                            setQuantities((prev) => ({
                                              ...prev,
                                              [key]: 0,
                                            }));
                                          } else {
                                            const numericValue = parseInt(
                                              value,
                                              10,
                                            );
                                            if (
                                              !isNaN(numericValue) &&
                                              numericValue >= 0
                                            ) {
                                              setQuantities((prev) => ({
                                                ...prev,
                                                [key]: numericValue,
                                              }));
                                            }
                                          }
                                        }}
                                      />

                                      <IconButton
                                        onClick={() =>
                                          setQuantities((prev) => ({
                                            ...prev,
                                            [`${product.productId}_${variantKey}`]:
                                              (prev[
                                                `${product.productId}_${variantKey}`
                                              ] || (fromExisting ? 0 : 1)) + 1,
                                          }))
                                        }
                                        disabled={
                                          fromExisting && popupMode === "pack"
                                        }
                                        sx={{
                                          border: `1px solid ${theme.palette.customColors.greyDarkborder}`,
                                          borderRadius: "4px",
                                          backgroundColor:
                                            theme.palette.customColors
                                              .grayBackGroundColor,
                                        }}
                                      >
                                        <AddIcon sx={{ fontSize: "14px" }} />
                                      </IconButton>
                                    </Box>
                                  </Box>

                                  <Box
                                    sx={{
                                      minWidth: "150px",
                                      maxWidth: "150px",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body2"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                      style={{
                                        marginBottom: 8,
                                        fontWeight: 600,
                                      }}
                                    >
                                      Pack Size
                                    </CustomTypography>
                                    <CustomTextField
                                      allowDecimalValue={false}
                                      allowNegativeValue={false}
                                      borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
                                      borderRadius={1}
                                      value={(
                                        variantPackSizes[
                                        getVariantStateKey(
                                          product.productId,
                                          variantKey,
                                        )
                                        ] || 1
                                      ).toString()}
                                      placeholder="Pack Size"
                                      width="100%"
                                      type="number"
                                      onChange={(e) => {
                                        const value = parseInt(
                                          e.target.value,
                                          10,
                                        );
                                        const nextValue =
                                          !isNaN(value) && value > 0
                                            ? value
                                            : 1;
                                        setVariantPackSizes((prev) => ({
                                          ...prev,
                                          [getVariantStateKey(
                                            product.productId,
                                            variantKey,
                                          )]: nextValue,
                                        }));
                                      }}
                                      disabled={
                                        fromExisting && popupMode === "pack"
                                      }
                                    />
                                  </Box>

                                  <Box
                                    sx={{
                                      minWidth: "170px",
                                      maxWidth: "170px",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body2"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                      style={{
                                        marginBottom: 8,
                                        fontWeight: 600,
                                      }}
                                    >
                                      Classification
                                    </CustomTypography>

                                    <AppSearchDropdownMulti
                                      borderColor={
                                        theme.palette.customColors.blackBorder
                                      }
                                      isMultiSelect={false}
                                      title="Select Classification"
                                      fontSize="0.8rem"
                                      value={getClassificationSelection(
                                        variantClassifications[
                                        getVariantStateKey(
                                          product.productId,
                                          variantKey,
                                        )
                                        ] as FilterValue | string | undefined,
                                      )}
                                      list={PACK_CLASSIFICATION_OPTIONS}
                                      local={true}
                                      handleSelect={(selectedItems) => {
                                        setVariantClassifications((prev) => ({
                                          ...prev,
                                          [getVariantStateKey(
                                            product.productId,
                                            variantKey,
                                          )]: selectedItems[0],
                                        }));
                                      }}
                                    />
                                  </Box>

                                  <Box
                                    sx={{
                                      minWidth: "150px",
                                      maxWidth: "150px",
                                      flexGrow: 1,
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body2"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                      style={{
                                        marginBottom: 8,
                                        fontWeight: 600,
                                      }}
                                    >
                                      SKU
                                    </CustomTypography>
                                    <CustomTextField
                                      borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
                                      borderRadius={1}
                                      value={
                                        projectPackagingOpsStore.skus?.[
                                        `${product.productId}_${variantKey}`
                                        ] || ""
                                      }
                                      placeholder={"SECI-II-PI-Size"}
                                      width="100%"
                                      type="text"
                                      disabled={
                                        fromExisting && popupMode === "pack"
                                      }
                                      onChange={(e) =>
                                        handleSkuChange(
                                          `${product.productId}_${variantKey}`,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </Box>

                                  <Box sx={{ paddingTop: "28px" }}>
                                    <IconButton
                                      color="secondary"
                                      disabled={
                                        fromExisting && popupMode === "pack"
                                      }
                                      sx={{
                                        backgroundColor:
                                          theme.palette.customColors.pinkLight,
                                        width: 30,
                                        height: 30,
                                      }}
                                      onClick={() =>
                                        handleRemoveVariant(
                                          product.productId,
                                          variantKey,
                                        )
                                      }
                                    >
                                      <DeleteIcon
                                        sx={{
                                          color:
                                            theme.palette.customColors.pinkDark,
                                          width: 20,
                                          height: 20,
                                        }}
                                      />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ),
                            )}
                          </>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyItems: "space-between !important",
                        justifyContent: "space-between !important",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "flex-start", sm: "flex-end" },
                        }}
                      >
                        <CustomTypography variant="heading7">
                          <span style={{ fontWeight: 300 }}>In stock: </span>
                          {availableQuantities[product.productId] !== undefined
                            ? availableQuantities[product.productId]
                            : isLoading
                              ? "Loading..."
                              : "N/A"}
                        </CustomTypography>
                      </Box>

                      {product.options?.Size?.length > 0 && (
                        <Box
                          display="flex"
                          flexWrap="wrap"
                          alignItems="flex-start" // always flex-start
                          justifyContent={"flex-start"} // always flex-start
                          gap={2}
                          sx={{
                            color: theme.palette.customColors.pinkDark,
                            fontWeight: 600,
                          }}
                        >
                          {!isMultipleProducts &&
                            product.options?.Size?.length > 1 && (
                              <AppButton
                                label="Add Variant"
                                onClick={() =>
                                  handleAddVariant(product.productId)
                                }
                                disabled={fromExisting && popupMode === "pack"}
                                customStyles={{
                                  backgroundColor: "transparent",
                                  padding: 1,
                                  margin: 0,
                                  fontSize: "0.8rem",
                                  color: theme.palette.customColors.pinkDark,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: theme.palette.customColors.pinkDark,
                                    boxShadow: "none",
                                  },
                                }}
                              />
                            )}
                          {!fromExisting && (
                            <AppButton
                              label="Delete"
                              onClick={() =>
                                handleDeleteProduct(product.productId)
                              }
                              disabled={fromExisting && popupMode === "pack"}
                              customStyles={{
                                backgroundColor: "transparent",
                                padding: 1,
                                margin: 0,
                                fontSize: "0.8rem",
                                color: theme.palette.customColors.pinkDark,
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: theme.palette.customColors.pinkDark,
                                  boxShadow: "none",
                                },
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CustomTypography variant="body1" color="textSecondary">
                  No products selected. Please add products to continue.
                </CustomTypography>
              </Box>
            )}
          </Card>
        </Box>

        <Box
          sx={{
            marginTop: 4,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            marginBottom: 8,
          }}
        >
          <AppButton
            onClick={() => {
              navigate(`/projects/${projectId}?tab_index=3`);
            }}
            label="Discard"
            customStyles={{
              height: "2.5rem",
              minWidth: "11.438rem",
              border: `1px solid ${theme.palette.customColors.pinkDark}`,
              backgroundColor: theme.palette.common.white,
              color: theme.palette.customColors.pinkDark,
            }}
            variant="grayscale"
          />

          <AppButton
            label={
              fromExisting
                ? isMultipleProducts
                  ? "Add Quantity / Edit Pack label"
                  : "Add Quantity / Edit Pack label"
                : isMultipleProducts
                  ? "Create Pack"
                  : "Create Bulk"
            }
            onClick={handleCreatePack}
            customStyles={{
              height: "2.5rem",
              minWidth: "11.438rem",
              backgroundColor: theme.palette.customColors.purple,
              color: theme.palette.customColors.white,
            }}
            variant="primary"
          />
        </Box>

        <CustomSucessPopup
          open={open}
          icon={CheckCircleIcon}
          title={
            fromExisting
              ? calculateBulkTotalQuantity() === 0
                ? "Pack Updated!"
                : "Quantity Added!"
              : `${modeDisplay} Created!`
          }
          description={
            fromExisting
              ? calculateBulkTotalQuantity() === 0
                ? `Great job! You have successfully updated the pack`
                : `Great job! You have successfully added quantity to the pack. Team can now proceed with packing.`
              : `Great job! You have successfully created a ${modeDisplay}. Team can now proceed with packing.`
          }
          primaryButtonText={
            fromExisting
              ? calculateBulkTotalQuantity() === 0
                ? "Update Complete"
                : "Addition Complete"
              : "Creation Complete"
          }
          onPrimaryClick={() => {
            setOpen(false);
            navigate(`/projects/${projectId}?tab_index=3`);
          }}
        />

        <SelectProductPopup
          open={productPopupOpen}
          mode={popupMode}
          products={
            Array.isArray(filteredProductsForPopup)
              ? filteredProductsForPopup.map((productData) => {
                const availableQuantity = productData?.availableQuantity || 0;

                return {
                  ...productData,
                  image_url: productData?.image_url || "",
                  product_name: productData?.name || "",
                  product_id: String(productData?.productId || ""),
                  quantity: availableQuantity,
                };
              })
              : []
          }
          selectedProductIds={productIdsFromUrl.map(Number)}
          onClose={handleCancelProduct}
          onConfirm={handleAddProduct}
          onClearAll={handleClearAll}
        />
      </Container>
    </>
  );
};

export default observer(CreatePack);
